import React, { useMemo } from 'react';
import { DollarSign, Package, Activity, Users, CalendarCheck, ArrowUpRight, ArrowDownRight, ShoppingCart } from "lucide-react";
import DailySalesReport from './DailySalesReport';

const ITEM_COLORS = {
  "LPG 20kg":   "#185FA5",
  "LPG 50kg":   "#7C3AED",
  "산소(O2)":   "#059669",
  "질소(N2)":   "#D97706",
  "아르곤(Ar)": "#DC2626",
  "탄산(CO2)":  "#16A34A",
};

const Dashboard = ({ deliveries = [], collections = [], customers = [], cylinders = [] }) => {
  const today = new Date().toISOString().split('T')[0];
  const currentMonth = today.slice(0, 7);

  const todayDisplay = (() => {
    const d = new Date();
    return `${d.getMonth() + 1}월 ${d.getDate()}일`;
  })();

  // 금일 통계
  const todayDeliveries  = deliveries.filter(d => d.date === today);
  const todayCollections = collections.filter(c => c.date === today);
  const todaySales       = todayDeliveries.reduce((s, d) => s + (d.amount || 0), 0);
  const todayPurchase    = todayCollections.reduce((s, c) => s + (c.amount || 0), 0);

  // 금월 통계
  const monthDeliveries   = useMemo(() => deliveries.filter(d => d.date?.startsWith(currentMonth)), [deliveries, currentMonth]);
  const monthCollections  = useMemo(() => collections.filter(c => c.date?.startsWith(currentMonth)), [collections, currentMonth]);
  const monthlySalesAmt   = monthDeliveries.reduce((s, d) => s + (d.amount || 0), 0);
  const monthlyPurchaseAmt = monthCollections.reduce((s, c) => s + (c.amount || 0), 0);
  const monthlyQty        = monthDeliveries.reduce((s, d) => s + (d.quantity || 0), 0);
  const activeCustomers   = new Set(monthDeliveries.map(d => d.customer)).size;

  // 공병 회수율
  const totalOut      = cylinders.filter(c => c.locType === 'customer').length;
  const totalInWarehouse = cylinders.filter(c => c.locType === 'warehouse').length;
  const recoveryRate  = (totalOut + totalInWarehouse) > 0
    ? Math.round((totalInWarehouse / (totalOut + totalInWarehouse)) * 100)
    : 100;

  // 월별 매출 추이 (최근 6개월)
  const monthlyChartData = useMemo(() => {
    return [...Array(6)].map((_, i) => {
      const d = new Date();
      d.setMonth(d.getMonth() - (5 - i));
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = `${d.getMonth() + 1}월`;
      const value = deliveries
        .filter(del => del.date?.startsWith(m))
        .reduce((s, del) => s + (del.amount || 0), 0);
      return { label, value };
    });
  }, [deliveries]);

  const maxChartVal = Math.max(...monthlyChartData.map(m => m.value), 1);

  // 품목별 판매 실적 (파이차트용)
  const itemSales = useMemo(() => {
    const map = {};
    deliveries.forEach(d => {
      if (d.items && typeof d.items === 'object') {
        Object.entries(d.items).forEach(([item, cnt]) => {
          map[item] = (map[item] || 0) + cnt;
        });
      } else if (d.item && d.quantity) {
        map[d.item] = (map[d.item] || 0) + d.quantity;
      }
    });
    return Object.entries(map)
      .map(([name, count]) => ({ name, count, color: ITEM_COLORS[name] || '#888' }))
      .sort((a, b) => b.count - a.count);
  }, [deliveries]);

  const fmt = (n) => n >= 10000
    ? `${(n / 10000).toFixed(n % 10000 === 0 ? 0 : 1)}만원`
    : `${n.toLocaleString()}원`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* 일일 판매일보 다운로드 배너 */}
      <DailySalesReport deliveries={deliveries} collections={collections} customers={customers} />

      {/* 상단 요약 카드 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 20 }}>
        <SummaryCard icon={DollarSign}   label="금월 누적 매출" value={fmt(monthlySalesAmt)}    color="#185FA5" />
        <SummaryCard icon={ShoppingCart} label="금월 누적 매입" value={fmt(monthlyPurchaseAmt)} color="#059669" />
        <SummaryCard icon={Package}      label="금월 판매량"    value={`${monthlyQty}본`}        color="#7C3AED" />
        <SummaryCard icon={Activity}     label="창고 보관율"    value={`${recoveryRate}%`}       color="#DC2626" />
        <SummaryCard icon={Users}        label="이달 거래처"    value={`${activeCustomers}곳`}   color="#D97706" />
      </div>

      {/* 금일 실적 */}
      <div style={todaySection}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <CalendarCheck size={22} color="#185FA5" />
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>금일 업무 실적 ({todayDisplay})</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div style={todayCard('#185FA5')}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={todayLabel}>오늘 배송(판매) 금액</span>
              <ArrowUpRight size={20} color="#185FA5" />
            </div>
            <div style={{ ...todayValue, color: '#1e293b' }}>
              {todaySales.toLocaleString()} <span style={unit}>원</span>
            </div>
            <div style={todaySubText}>오늘 총 {todayDeliveries.length}건 배송됨</div>
          </div>
          <div style={todayCard('#059669')}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={todayLabel}>오늘 매입(회수) 금액</span>
              <ArrowDownRight size={20} color="#059669" />
            </div>
            <div style={{ ...todayValue, color: '#059669' }}>
              {todayPurchase.toLocaleString()} <span style={unit}>원</span>
            </div>
            <div style={todaySubText}>오늘 총 {todayCollections.length}건 회수됨</div>
          </div>
        </div>
      </div>

      {/* 그래프 구역 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 24 }}>

        {/* 월별 매출 추이 */}
        <div style={chartBox}>
          <h3 style={chartTitle}>📈 월별 매출 추이</h3>
          {monthlyChartData.every(m => m.value === 0) ? (
            <EmptyState text="배송 실적이 없습니다" />
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', height: 180, borderBottom: '2px solid #f0f0f0', paddingBottom: 0 }}>
              {monthlyChartData.map((m, i) => {
                const h = Math.max((m.value / maxChartVal) * 160, m.value > 0 ? 4 : 0);
                return (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                    {m.value > 0 && <span style={{ fontSize: 11, color: '#185FA5', fontWeight: 700, marginBottom: 4 }}>{fmt(m.value)}</span>}
                    <div style={{ width: 28, height: h, background: i === 5 ? '#185FA5' : '#93b8df', borderRadius: '4px 4px 0 0', minHeight: m.value > 0 ? 4 : 0 }} />
                    <span style={{ marginTop: 8, fontSize: 12, color: i === 5 ? '#185FA5' : '#777', fontWeight: i === 5 ? 700 : 400 }}>{m.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 품목별 판매 실적 */}
        <div style={chartBox}>
          <h3 style={chartTitle}>🥧 품목별 판매 실적</h3>
          {itemSales.length === 0
            ? <EmptyState text="판매 실적이 없습니다" />
            : <PieChart data={itemSales} />
          }
        </div>
      </div>

      {/* 최근 배송 이력 */}
      {deliveries.length > 0 && (
        <div style={chartBox}>
          <h3 style={{ ...chartTitle, marginBottom: 16 }}>🚚 최근 배송 내역</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f7f2', borderBottom: '1px solid #e5e3db' }}>
                {['날짜', '거래처', '품목', '수량', '금액'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 13, color: '#777', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...deliveries].reverse().slice(0, 10).map(d => (
                <tr key={d.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={tdStyle}>{d.date}</td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: '#185FA5' }}>{d.customer}</td>
                  <td style={tdStyle}>{d.items ? Object.entries(d.items).map(([k, v]) => `${k} ${v}본`).join(' / ') : `${d.item} ${d.quantity}본`}</td>
                  <td style={tdStyle}>{d.quantity}본</td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: '#1e293b' }}>{(d.amount || 0).toLocaleString()}원</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const PieChart = ({ data }) => {
  const total = data.reduce((s, d) => s + d.count, 0);
  const cx = 90, cy = 90, r = 75;
  let cumAngle = -Math.PI / 2;

  const slices = data.map(item => {
    const angle = (item.count / total) * 2 * Math.PI;
    const start = cumAngle;
    cumAngle += angle;
    const end = cumAngle;
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const largeArc = angle > Math.PI ? 1 : 0;
    return {
      ...item,
      path: `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`,
      pct: Math.round((item.count / total) * 100),
    };
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
      <svg width={180} height={180}>
        {slices.map((s, i) => (
          <path key={i} d={s.path} fill={s.color} stroke="white" strokeWidth={2} />
        ))}
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
        {slices.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: s.color, flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#333', flex: 1 }}>{s.name}</span>
            <span style={{ fontSize: 12, color: '#888' }}>{s.pct}%</span>
            <span style={{ fontSize: 13, color: s.color, fontWeight: 800, minWidth: 42, textAlign: 'right' }}>{s.count}본</span>
          </div>
        ))}
        <div style={{ borderTop: '1px solid #eee', paddingTop: 10, marginTop: 2, display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13, color: '#555', fontWeight: 600 }}>합계</span>
          <span style={{ fontSize: 13, color: '#185FA5', fontWeight: 800 }}>{total}본</span>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ text }) => (
  <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: 14, border: '1px dashed #e5e3db', borderRadius: 10 }}>
    {text}
  </div>
);

const SummaryCard = ({ icon: Icon, label, value, color }) => (
  <div style={{ background: 'white', padding: 20, borderRadius: 16, border: '1px solid #d3d1c7', display: 'flex', alignItems: 'center', gap: 15 }}>
    <div style={{ background: `${color}18`, padding: 12, borderRadius: 12 }}><Icon size={24} color={color} /></div>
    <div>
      <p style={{ margin: 0, fontSize: 13, color: '#666' }}>{label}</p>
      <p style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>{value}</p>
    </div>
  </div>
);

const todaySection = { background: '#fff', padding: 25, borderRadius: 16, border: '1px solid #d3d1c7' };
const todayCard = (color) => ({ background: '#f8fafc', padding: 20, borderRadius: 12, borderLeft: `6px solid ${color}` });
const todayLabel   = { fontSize: 14, color: '#64748b', fontWeight: 600 };
const todayValue   = { fontSize: 28, fontWeight: 900, margin: '10px 0' };
const unit         = { fontSize: 16, fontWeight: 400 };
const todaySubText = { fontSize: 13, color: '#94a3b8' };
const chartBox     = { background: 'white', padding: 30, borderRadius: 16, border: '1px solid #d3d1c7' };
const chartTitle   = { margin: '0 0 20px 0', fontSize: 16, fontWeight: 700 };
const tdStyle      = { padding: '11px 14px', fontSize: 13, color: '#444' };

export default Dashboard;
